import { TrendingUp, TrendingDown, Minus, Activity } from "lucide-react";
import { cn } from "@/lib/utils";

interface StatItemProps {
  label: string;
  value: string | number;
  unit: string;
  metricColor: string;
}

const StatItem = ({ label, value, unit, metricColor }: StatItemProps) => {
  return (
    <div className="flex flex-col">
      <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">
        {label}
      </span>
      <div className="flex items-baseline gap-1 mt-0.5">
        <span className="text-lg font-bold text-foreground tabular-nums">
          {value}
        </span>
        <span className="text-xs text-muted-foreground">{unit}</span>
      </div>
    </div>
  );
};

interface MiniSparklineProps {
  data: number[];
  color: string;
  width?: number;
  height?: number;
}

const MiniSparkline = ({
  data,
  color,
  width = 80,
  height = 28,
}: MiniSparklineProps) => {
  if (data.length < 2) return null;

  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;

  const points = data
    .map((value, index) => {
      const x = (index / (data.length - 1)) * width;
      const y = height - ((value - min) / range) * height;
      return `${x},${y}`;
    })
    .join(" ");

  return (
    <svg width={width} height={height} className="overflow-visible">
      <polyline
        points={points}
        fill="none"
        stroke={color}
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {/* End dot */}
      <circle
        cx={width}
        cy={height - ((data[data.length - 1] - min) / range) * height}
        r={3}
        fill={color}
      />
    </svg>
  );
};

interface StatsOverviewProps {
  current: number;
  average: number;
  min: number;
  max: number;
  unit: string;
  metricColor: string;
  trend?: {
    direction: "up" | "down" | "neutral";
    value: string;
  };
  chartData?: number[];
}

export const StatsOverview = ({
  current,
  average,
  min,
  max,
  unit,
  metricColor,
  trend,
  chartData,
}: StatsOverviewProps) => {
  const TrendIcon =
    trend?.direction === "up"
      ? TrendingUp
      : trend?.direction === "down"
        ? TrendingDown
        : Minus;
  const trendColor =
    trend?.direction === "up"
      ? "text-success"
      : trend?.direction === "down"
        ? "text-destructive"
        : "text-muted-foreground";

  return (
    <div className="bg-card border border-border rounded-xl p-4 space-y-3">
      {/* Header */}
      <div className="flex items-center gap-2">
        <Activity className="w-4 h-4 text-muted-foreground" />
        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
          Statistics
        </span>
      </div>

      {/* Stats Grid */}
      <div className="flex items-center justify-between">
        <div className="grid grid-cols-3 gap-x-6 gap-y-2">
          <StatItem
            label="Current"
            value={current}
            unit={unit}
            metricColor={metricColor}
          />
          <StatItem
            label="Average"
            value={average}
            unit={unit}
            metricColor={metricColor}
          />
          <div className="flex flex-col">
            <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">
              Range
            </span>
            <div className="flex items-center gap-1 mt-0.5">
              <span className="text-lg font-bold text-foreground tabular-nums">
                {min}
              </span>
              <span className="text-muted-foreground">–</span>
              <span className="text-lg font-bold text-foreground tabular-nums">
                {max}
              </span>
              <span className="text-xs text-muted-foreground ml-0.5">
                {unit}
              </span>
            </div>
          </div>
        </div>

        {/* Trend & Sparkline */}
        <div className="flex items-center gap-4">
          {chartData && chartData.length > 1 && (
            <MiniSparkline data={chartData} color={metricColor} />
          )}
          {trend && (
            <div
              className={cn(
                "flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium",
                trend.direction === "up" && "bg-success/10 text-success",
                trend.direction === "down" &&
                  "bg-destructive/10 text-destructive",
                trend.direction === "neutral" &&
                  "bg-muted text-muted-foreground"
              )}
            >
              <TrendIcon className="w-3.5 h-3.5" />
              <span>{trend.value}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
